package com.downdogapp.client.singleton

import com.downdogapp.*
import com.downdogapp.client.singleton.CastState.*
import com.downdogapp.client.singleton.IdleReason.ERROR
import com.downdogapp.client.singleton.IdleReason.FINISHED
import kotlin.js.ExperimentalJsExport
import kotlin.js.JsExport

object Cast : SharedCastInterface by CastHelper {

  var pendingLoad: LoadParams? = null
  var prevCastState = castState
  var prevPlayerState: PlayerState? = null // null at first, but never again

  val isCasting
    get() = castState in setOf(CONNECTING, CONNECTED)

  fun isStarting(old: CastState, new: CastState) =
    old in setOf(NO_DEVICES_AVAILABLE, NOT_CONNECTED)
    && new in setOf(CONNECTING, CONNECTED)

  fun isEnding(old: CastState, new: CastState) =
    old in setOf(CONNECTING, CONNECTED)
    && new in setOf(NO_DEVICES_AVAILABLE, NOT_CONNECTED)

  fun load(loadParams: LoadParams) {
    pendingLoad = null
    if (castState == CONNECTED
        && !CastHelper.isLoading) {
      CastHelper.loadInternal(loadParams)
    } else {
      // Wait for PLAYING or PAUSED state.
      // Otherwise, the load will return a session_error, and the sender will lose the ability to control the receiver
      // (despite remaining in a CONNECTED cast state and eventually returning to a PLAYING or PAUSED player state).
      pendingLoad = loadParams
    }
  }

  // The platform implementations of CastHelper are responsible for setting up
  // listeners so that these two methods are called appropriately
  fun onCastStateChanged() {
    if (castState != prevCastState) { // just in case
      eventHandler?.onCastStateChanged(prevCastState, castState)
      prevCastState = castState
      pendingLoad?.let { // for android
        load(it)
      }
    }
  }

  fun onPlayerStateChanged() {
    if (playerState != prevPlayerState) { // this check is needed on Android and iOS
      prevPlayerState = playerState
      if (CastHelper.idleReason in setOf(FINISHED, ERROR)) {
        stop()
      }
      eventHandler?.onPlayerStateChanged()
      pendingLoad?.let {
        load(it)
      }
    }
  }
}

// The platform implementations of CastHelper are responsible for setting up
// listeners so that these two methods are called appropriately
@ExperimentalJsExport
@JsExport
fun onCastStateChanged() {
  Cast.onCastStateChanged()
}

@ExperimentalJsExport
@JsExport
fun onCastPlayerStateChanged() {
  Cast.onPlayerStateChanged()
}

enum class CastState {
  NO_DEVICES_AVAILABLE,
  NOT_CONNECTED,
  CONNECTING,
  CONNECTED;
}

enum class PlayerState {
  PLAYING,
  PAUSED,
  BUFFERING,
  NO_MEDIA,
  NO_PLAYER
}

data class LoadParams(val title: String,
                      val videoUrl: String,
                      val playbackStartTime: Duration)

enum class IdleReason {
  CANCELLED,
  INTERRUPTED,
  FINISHED,
  ERROR,
  NOT_IDLE
}

interface CastEventHandler {

  fun onCastStateChanged(old: CastState, new: CastState)
  fun onPlayerStateChanged()
}

interface SharedCastInterface {

  val castState: CastState
  val playerState: PlayerState
  val receiverName: String?
  var playerTime: Double?
  var eventHandler: CastEventHandler?
  fun play()
  fun pause()
  fun stop()
}

interface CastHelperInterface : SharedCastInterface {

  val idleReason: IdleReason
  val isLoading: Boolean // for iOS
  fun loadInternal(load: LoadParams)
}

expect object CastHelper : CastHelperInterface
